class UlifHeaderSearch extends HTMLElement {
  #abortController = new AbortController();
  #mutationObserver;
  #component;
  #input;
  #resultsId;
  #suppressFocusOpen = false;
  #pageScrollY = null;
  #restoringPageScroll = false;

  connectedCallback() {
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#component = this.querySelector('predictive-search-component');
    this.#input = this.querySelector('[ref="searchInput"]');
    this.#resultsId = this.dataset.resultsId;

    if (!this.#component || !this.#input || !this.#resultsId) return;

    const { signal } = this.#abortController;

    this.#input.addEventListener('focus', this.#open, { signal });
    this.addEventListener('pointerdown', this.#handleSearchPointerDown, { capture: true, signal });
    this.addEventListener('click', this.#handleSearchClick, { signal });
    this.addEventListener('keydown', this.#handleSearchKeydown, { capture: true, signal });
    this.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false, signal });
    document.addEventListener('click', this.#handleDocumentClick, { capture: true, signal });
    document.addEventListener('keydown', this.#handleGlobalKeydown, { capture: true, signal });
    document.addEventListener('megaMenu:hover', this.#handleMegaMenuHover, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
    window.addEventListener('scroll', this.#handleWindowScroll, { passive: true, signal });

    this.#mutationObserver = new MutationObserver(this.#syncResults);
    this.#mutationObserver.observe(this.#component, {
      attributes: true,
      attributeFilter: ['id'],
      childList: true,
      subtree: true,
    });

    this.#syncResults();
    requestAnimationFrame(this.#syncOverlayTop);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#mutationObserver?.disconnect();

    if (this.hasAttribute('data-open')) {
      this.removeAttribute('data-open');
      this.#releasePageLock();
    }
  }

  #isVisibleLayout = () => {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    return this.dataset.layout === 'mobile' ? isMobile : !isMobile;
  };

  #open = () => {
    if (this.#suppressFocusOpen) {
      queueMicrotask(this.#restoreClosedState);
      return;
    }

    if (!this.#isVisibleLayout()) return;

    for (const search of document.querySelectorAll('ulif-header-search[data-open]')) {
      if (search !== this && typeof search.closeSearch === 'function') {
        search.closeSearch(false);
      }
    }

    if (!this.hasAttribute('data-open')) {
      this.#pageScrollY = window.scrollY;
    }

    this.#syncOverlayTop();
    this.setAttribute('data-open', '');
    this.#input.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ulif-search-is-open');
    requestAnimationFrame(this.#syncOverlayTop);
  };

  closeSearch = (restoreFocus = false) => {
    if (!this.#component || !this.#input) return;

    this.#suppressFocusOpen = true;
    this.#restoreClosedState();

    if (typeof this.#component.resetSearch === 'function') {
      this.#component.resetSearch(false);
    } else {
      this.#input.value = '';
    }

    if (restoreFocus) {
      this.#input.focus({ preventScroll: true });
    }
  };

  #restoreClosedState = () => {
    if (!this.#suppressFocusOpen) return;

    this.removeAttribute('data-open');
    this.#component?.classList.remove('predictive-search--expanded');
    this.#input?.setAttribute('aria-expanded', 'false');
    this.#input?.blur();
    this.#releasePageLock();
  };

  #releasePageLock = () => {
    if (!document.querySelector('ulif-header-search[data-open]')) {
      document.documentElement.classList.remove('ulif-search-is-open');
      this.#pageScrollY = null;
      this.#restoringPageScroll = false;
    }
  };

  #handleSearchPointerDown = (event) => {
    if (event.target instanceof Element && event.target.closest('[ref="searchInput"]')) {
      this.#suppressFocusOpen = false;
    }
  };

  #handleSearchClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-ulif-search-close], [data-ulif-search-dismiss]') : null;
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeSearch(false);
  };

  #handleDocumentClick = (event) => {
    if (!this.hasAttribute('data-open') || event.composedPath().includes(this)) return;
    this.closeSearch(false);
  };

  #handleSearchKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('data-open')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.closeSearch(false);
    }
  };

  #handleWheel = (event) => {
    if (!this.hasAttribute('data-open')) return;

    const target = event.target instanceof Element ? event.target : null;
    const scroller = target?.closest('.predictive-search-form__content');

    if (!(scroller instanceof HTMLElement) || !this.contains(scroller)) {
      event.preventDefault();
      return;
    }

    const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
    const scrollingPastTop = event.deltaY < 0 && scroller.scrollTop <= 0;
    const scrollingPastBottom = event.deltaY > 0 && scroller.scrollTop >= maxScrollTop - 1;

    if (maxScrollTop <= 0 || scrollingPastTop || scrollingPastBottom) {
      event.preventDefault();
    }
  };

  #handleTouchMove = (event) => {
    if (!this.hasAttribute('data-open')) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.predictive-search-form__content')) {
      event.preventDefault();
    }
  };

  #handleWindowScroll = () => {
    this.#syncOverlayTop();

    if (
      !this.hasAttribute('data-open') ||
      this.#pageScrollY === null ||
      this.#restoringPageScroll ||
      Math.abs(window.scrollY - this.#pageScrollY) < 1
    ) {
      return;
    }

    this.#restoringPageScroll = true;
    window.scrollTo(window.scrollX, this.#pageScrollY);
    requestAnimationFrame(() => {
      this.#restoringPageScroll = false;
      this.#syncOverlayTop();
    });
  };

  #handleGlobalKeydown = (event) => {
    if (event.key === 'Tab') {
      this.#suppressFocusOpen = false;
      return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && this.#isVisibleLayout()) {
      event.preventDefault();
      this.#suppressFocusOpen = false;
      this.#input.focus({ preventScroll: true });
    }
  };

  #handleResize = () => {
    if (this.hasAttribute('data-open') && !this.#isVisibleLayout()) {
      this.closeSearch(false);
    }

    this.#syncOverlayTop();
  };

  #syncOverlayTop = () => {
    if (!this.#isVisibleLayout()) return;

    const headerRow = this.closest('.header__row');
    const header = headerRow instanceof HTMLElement ? headerRow : document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const overlayTop = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    this.style.setProperty('--ulif-search-overlay-top', `${overlayTop}px`);
    document.documentElement.style.setProperty('--ulif-visible-header-bottom', `${overlayTop}px`);
  };

  #handleMegaMenuHover = () => {
    if (this.hasAttribute('data-open')) {
      this.closeSearch(false);
    }
  };

  #syncResults = () => {
    this.#restoreClosedState();

    const resultsHost = this.querySelector('[ref="predictiveSearchResults"]');
    if (!resultsHost) return;

    const controlledResults = resultsHost.matches('.predictive-search-dropdown')
      ? resultsHost
      : resultsHost.querySelector('.predictive-search-dropdown');

    if (controlledResults && controlledResults.id !== this.#resultsId) {
      controlledResults.id = this.#resultsId;
    }

    this.#input?.setAttribute('aria-controls', this.#resultsId);
    this.#input?.setAttribute('aria-owns', this.#resultsId);

    const productTarget = resultsHost.querySelector('#predictive-search-products, [data-ulif-recent-target]');
    if (productTarget) {
      const productTargetId = `${this.#resultsId}-products`;
      if (productTarget.id !== productTargetId) productTarget.id = productTargetId;
    }

    const queryList = resultsHost.querySelector('.predictive-search-results__wrapper-queries');
    if (queryList && !queryList.previousElementSibling?.matches('[data-ulif-matches-title]')) {
      const title = document.createElement('h2');
      title.id = `${this.#resultsId}-queries`;
      title.className = 'predictive-search-results__title ulif-search-results__matches-title';
      title.dataset.ulifMatchesTitle = '';
      title.textContent = 'Matches';
      queryList.setAttribute('aria-labelledby', title.id);
      queryList.before(title);
    }
  };
}

if (!customElements.get('ulif-header-search')) {
  customElements.define('ulif-header-search', UlifHeaderSearch);
}
